import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Tabs,
  Tag,
  Button,
  Descriptions,
  Spin,
  Table,
  Tooltip,
  Space,
  Input,
  Empty,
  Row,
  Col,
  Dropdown,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  CheckOutlined,
  MoreOutlined,
  FileTextOutlined,
  DollarOutlined,
  FolderOpenOutlined,
  CheckCircleFilled,
  CloseCircleOutlined,
  WarningOutlined,
  SafetyCertificateOutlined,
  EyeOutlined,
  UserOutlined,
  InboxOutlined,
} from "@ant-design/icons";
import Layout from "../../components/Layout";
import axiosInstance from "../../variables/api/axiosInstance";
import ChecklistPieces from "../../components/ChecklistPieces";
import {
  formatDate,
  formatMontant,
  TYPES_DEPENSE,
  STATUT_FACTURE_MAP,
  STATUT_DOSSIER_MAP,
  TYPES_DOC_GED_MAP,
  can,
} from "../../variables/constants";
import { HAB } from "../../variables/habilitations";
import { toast } from "react-toastify";
import NouvelleFactureModal from "./NouvelleFactureModal";
import CBModal from '../../components/CBModal';
import DocumentPreviewModal from '../sinistres/components/DocumentPreviewModal';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;

const GREEN = "#1a6b3c";
const GREEN_PALE = "#e8f5ed";

const labelDepense = (code) =>
  TYPES_DEPENSE.find((t) => t.value === code)?.label || code;
const iconDepense = (code) =>
  TYPES_DEPENSE.find((t) => t.value === code)?.icon || <FileTextOutlined />;

/* ── KPI card ── */
const Kpi = ({ label, value, sub, color = GREEN, icon }) => (
  <div className="cb-card" style={{ padding: 18 }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 6,
      }}
    >
      {icon && (
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: GREEN_PALE,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
          }}
        >
          {icon}
        </div>
      )}
      <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>
        {label}
      </div>
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
    {sub && (
      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{sub}</div>
    )}
  </div>
);

/* ── Modal validation facture ── */
const ValiderFactureModal = ({ open, facture, onClose, onValidated }) => {
  const { t } = useTranslation('reclamations');
  const [mode, setMode] = useState("VALIDE");
  const [montantReclame, setMontantReclame] = useState(0);
  const [montantRetenu, setMontantRetenu] = useState(null);
  const [motif, setMotif] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && facture) {
      setMode("VALIDE");
      setMontantReclame(facture.montantReclame || 0);
      setMontantRetenu(facture.montantReclame || 0);
      setMotif("");
    }
  }, [open, facture]);

  // montant effectif : soit la valeur saisie (si facture à 0), soit le montant d'origine
  const effectifReclame = facture?.montantReclame > 0 ? facture.montantReclame : montantReclame;

  const confirmer = async () => {
    if (facture.montantReclame === 0 && (!montantReclame || montantReclame <= 0)) {
      toast.error(t('detail.valider.renseignerMontant'));
      return;
    }
    if (
      mode === "VALIDE_PARTIELLEMENT" &&
      (montantRetenu == null ||
        montantRetenu <= 0 ||
        montantRetenu >= effectifReclame)
    ) {
      toast.error(
        t('detail.valider.montantEntre', { max: effectifReclame }),
      );
      return;
    }
    if (
      (mode === "REJETE" || mode === "VALIDE_PARTIELLEMENT") &&
      !motif.trim()
    ) {
      toast.error(t('commun.motifRequis'));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        statutTraitement: mode,
        montantRetenu:
          mode === "REJETE"
            ? 0
            : mode === "VALIDE"
              ? effectifReclame
              : montantRetenu,
        motifRejet:
          mode === "REJETE" || mode === "VALIDE_PARTIELLEMENT"
            ? motif.trim()
            : null,
        // Envoyé uniquement si la facture était à 0 et que l'utilisateur a saisi un montant
        ...(facture.montantReclame === 0 && montantReclame > 0
          ? { montantReclame }
          : {}),
      };
      await axiosInstance.patch(
        `/v1/factures-reclamation/${facture.factureTrackingId}`,
        payload,
      );
      toast.success(t('detail.valider.succes'));
      await onValidated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('commun.erreur'));
    } finally {
      setLoading(false);
    }
  };

  if (!facture) return null;

  // Quand l'utilisateur change le montant réclamé, réinitialise le montant retenu
  const handleMontantReclamChange = (val) => {
    setMontantReclame(val);
    setMontantRetenu(val);
  };

  const modes = [
    {
      key: "VALIDE",
      label: t('detail.valider.modes.VALIDE'),
      color: GREEN,
      icon: <CheckOutlined />,
    },
    {
      key: "VALIDE_PARTIELLEMENT",
      label: t('detail.valider.modes.VALIDE_PARTIELLEMENT'),
      color: "#f97316",
      icon: <CheckCircleFilled />,
    },
    {
      key: "REJETE",
      label: t('detail.valider.modes.REJETE'),
      color: "#dc2626",
      icon: <CloseCircleOutlined />,
    },
  ];

  return (
    <CBModal
      title={t('detail.valider.titre')}
      open={open}
      onCancel={onClose}
      onOk={confirmer}
      confirmLoading={loading}
      okText={t('detail.valider.confirmer')}
      okButtonProps={{ style: { background: GREEN, borderColor: GREEN } }}
      width={560}
    >
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4 }}>
          {iconDepense(facture.typeDepense)} {labelDepense(facture.typeDepense)}{" "}
          - {facture.nomPrestataire}
        </div>
        {facture.montantReclame > 0 && (
          <div style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>
            {t('detail.valider.reclame', { montant: formatMontant(facture.montantReclame) })}
          </div>
        )}
      </div>

      {/* Champ montant réclamé - affiché uniquement pour les factures auto-créées (montant = 0) */}
      {facture.montantReclame === 0 && (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fcd34d' }}>
          <div style={{ fontSize: 12, color: '#92400e', fontWeight: 600, marginBottom: 8 }}>
            <WarningOutlined /> {t('detail.valider.montantNonRenseigne')}
          </div>
          <label style={{ fontSize: 12, fontWeight: 500, color: '#374151', display: 'block', marginBottom: 4 }}>
            {t('commun.montantReclameFcfa')} <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <Input
            type="number"
            min={1}
            value={montantReclame || ''}
            onChange={(e) => handleMontantReclamChange(Number(e.target.value))}
            placeholder={t('detail.valider.montantPlaceholder')}
            size="large"
            style={{ fontFamily: 'var(--font-main)', fontWeight: 600 }}
          />
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {modes.map((m) => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            style={{
              flex: 1,
              padding: "12px 8px",
              borderRadius: 10,
              cursor: "pointer",
              border: `2px solid ${mode === m.key ? m.color : "#e5e7eb"}`,
              background: mode === m.key ? `${m.color}10` : "#fff",
              color: mode === m.key ? m.color : "#6b7280",
              fontWeight: mode === m.key ? 600 : 400,
              fontSize: 12,
              transition: "all 180ms",
            }}
          >
            <div style={{ fontSize: 18, marginBottom: 4 }}>{m.icon}</div>
            {m.label}
          </button>
        ))}
      </div>

      {mode === "VALIDE" && (
        <div style={{ marginBottom: 12, padding: '12px 16px', background: '#e8f5ed', borderRadius: 8, border: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: 12, color: '#374151', marginBottom: 2, fontWeight: 500 }}>{t('detail.valider.montantRetenu')}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: effectifReclame > 0 ? GREEN : '#9ca3af' }}>
            {effectifReclame > 0 ? formatMontant(effectifReclame) : '- FCFA'}
          </div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{t('detail.valider.integralite')}</div>
        </div>
      )}

      {mode === "VALIDE_PARTIELLEMENT" && (
        <div style={{ marginBottom: 12 }}>
          <label
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: "#374151",
              display: "block",
              marginBottom: 4,
            }}
          >
            {t('commun.montantRetenuFcfa')}
          </label>
          <Input
            type="number"
            value={montantRetenu}
            onChange={(e) => setMontantRetenu(Number(e.target.value))}
            max={effectifReclame - 1}
            min={1}
          />
          <div style={{ fontSize: 11, color: "#6b7280", marginTop: 4 }}>
            {t('detail.valider.reduction', { montant: formatMontant(effectifReclame - (montantRetenu || 0)) })}
          </div>
        </div>
      )}

      {mode === "REJETE" && (
        <div style={{ marginBottom: 12, padding: '12px 16px', background: '#fef2f2', borderRadius: 8, border: '1px solid #fecaca' }}>
          <div style={{ fontSize: 12, color: '#374151', marginBottom: 2, fontWeight: 500 }}>{t('detail.valider.montantRetenu')}</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#dc2626' }}>{formatMontant(0)}</div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{t('detail.valider.aucunMontantRetenu')}</div>
        </div>
      )}

      {(mode === "REJETE" || mode === "VALIDE_PARTIELLEMENT") && (
        <div>
          <label
            style={{
              fontSize: 12,
              fontWeight: 500,
              color: "#374151",
              display: "block",
              marginBottom: 4,
            }}
          >
            {mode === "REJETE" ? t('commun.motifRejet') : t('commun.motifReduction')}
          </label>
          <TextArea
            rows={3}
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            placeholder={t('commun.motifPlaceholder')}
            maxLength={500}
            showCount
          />
        </div>
      )}
    </CBModal>
  );
};

/* ── Page détail ── */
/**
 * « Valider le passage à MÛR » - relance l'évaluation, et dit ce qui manque.
 *
 * POURQUOI UN BOUTON POUR UN CALCUL AUTOMATIQUE. La maturité est déjà recalculée
 * à chaque dépôt de pièce. Mais le rédacteur qui vient de compléter un dossier
 * n'avait aucun moyen de le CONSTATER : rien ne lui disait si le sinistre avait
 * basculé, ni, dans le cas contraire, ce qui manquait encore. Il en concluait
 * qu'il fallait forcer le statut à la main.
 *
 * CE N'EST PAS UN FORÇAGE, et le bouton ne doit jamais le laisser croire. Le
 * statut ne bascule que si les pièces obligatoires sont reçues - et la
 * consolidation acquise pour un dommage corporel. Sinon rien ne change, et le
 * motif s'affiche. MÛR continue de signifier « dossier complet », ce dont dépend
 * le serveur pour laisser partir un règlement.
 */
function BoutonMaturite({ sinistreTrackingId, victimeTrackingId, onChange }) {
  const { t } = useTranslation('reclamations');
  const [enCours, setEnCours] = useState(false);
  const [blocage, setBlocage] = useState(null);

  if (!can(HAB.RECLAMATION_UPDATE) || !sinistreTrackingId) return null;

  const evaluer = async () => {
    setEnCours(true);
    setBlocage(null);
    try {
      const res = await axiosInstance.post(
        `/v1/sinistres/${sinistreTrackingId}/maturite/reevaluation`,
      );
      const etat = res?.data?.data;
      /* Le motif de CETTE victime, pas celui du sinistre : la page est le dossier
         d'une personne, et lui montrer le blocage d'une autre l'enverrait
         chercher une pièce qui ne la concerne pas. */
      const moi = (etat?.victimes || []).find(
        (v) => v.victimeTrackingId === victimeTrackingId,
      );
      if (moi && !moi.estMur) {
        setBlocage(moi.motifBlocage || t('detail.maturite.dossierIncomplet'));
        toast.info(t('detail.maturite.pasEncoreMurMessage'));
      } else {
        toast.success(res?.data?.message || t('detail.maturite.reevaluee'));
      }
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('detail.maturite.impossible'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Space direction="vertical" size={4} align="end">
      <Tooltip title={t('detail.maturite.aide')}>
        <Button
          icon={<SafetyCertificateOutlined />}
          loading={enCours}
          onClick={evaluer}
          style={{ borderRadius: 8 }}
        >
          {t('detail.maturite.bouton')}
        </Button>
      </Tooltip>
      {blocage && (
        <Alert
          type="warning"
          showIcon
          style={{ borderRadius: 8, maxWidth: 420 }}
          message={t('detail.maturite.pasEncoreMur')}
          description={blocage}
          closable
          onClose={() => setBlocage(null)}
        />
      )}
    </Space>
  );
}

export default function DetailReclamation() {
  const { t } = useTranslation('reclamations');
  const { id } = useParams();
  const navigate = useNavigate();
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docsGed, setDocsGed] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [factureModal, setFactureModal] = useState(false);
  // Facture en cours de correction (null : aucune).
  const [factureACorriger, setFactureACorriger] = useState(null);
  const [validerModal, setValiderModal] = useState({
    open: false,
    facture: null,
  });

  const loadDossier = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/v1/dossiers-reclamation/${id}`);
      setDossier(res.data?.data || null);
    } catch {
      setDossier(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadDossier();
  }, [loadDossier]);

  // Sans le spinner de loadDossier, qui démonterait les onglets : après un
  // téléversement depuis la checklist, seuls les compteurs doivent bouger.
  const rafraichirDossier = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`/v1/dossiers-reclamation/${id}`);
      setDossier(res.data?.data || null);
    } catch { /* la checklist s'est déjà rechargée, le reste suivra au prochain affichage */ }
  }, [id]);

  const loadDocsGed = useCallback(async () => {
    if (!dossier?.victimeTrackingId) return;
    try {
      const res = await axiosInstance.get(`/v1/ged/victimes/${dossier.victimeTrackingId}/documents`);
      setDocsGed(res.data?.data || []);
    } catch {
      setDocsGed([]);
    }
  }, [dossier?.victimeTrackingId]);

  useEffect(() => {
    loadDocsGed();
  }, [loadDocsGed]);

  const supprimerFacture = async (factureId) => {
    try {
      await axiosInstance.delete(`/v1/factures-reclamation/${factureId}`);
      toast.success(t('detail.factures.supprimee'));
      loadDossier();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('commun.erreur'));
    }
  };

  const telecharger = async (ossanGedDocumentId, nom) => {
    if (!ossanGedDocumentId) {
      toast.warning(t('detail.messages.aucunFichier'));
      return;
    }
    try {
      const res = await axiosInstance.get(
        `/v1/ged/documents/${ossanGedDocumentId}/telecharger`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = nom || "document.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t('detail.messages.echecTelechargement'));
    }
  };

  if (loading) {
    return (
      <Layout title={t('detail.titreChargement')}>
        <div className="cb-loader">
          <Spin />
        </div>
      </Layout>
    );
  }
  if (!dossier) {
    return (
      <Layout title={t('detail.titreChargement')}>
        <Empty description={t('detail.introuvable')} style={{ marginTop: 60 }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/reclamations")}>
            {t('detail.retourListe')}
          </Button>
        </Empty>
      </Layout>
    );
  }

  const statut = STATUT_DOSSIER_MAP[dossier.statut] || {
    label: dossier.statut,
    color: "default",
  };

  /* ── Colonnes factures ── */
  const factureColumns = [
    {
      title: t('detail.factures.colonnes.depense'),
      key: "type",
      width: 180,
      render: (_, r) => (
        <div>
          <span style={{ marginRight: 6 }}>{iconDepense(r.typeDepense)}</span>
          <strong style={{ fontSize: 13 }}>
            {labelDepense(r.typeDepense)}
          </strong>
          {r.lienAvecAccidentVerifie && (
            <Tooltip title={t('detail.factures.lienVerifie')}>
              <CheckCircleFilled
                style={{ color: GREEN, marginLeft: 6, fontSize: 12 }}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: t('detail.factures.colonnes.prestataire'),
      dataIndex: "nomPrestataire",
      key: "prest",
      ellipsis: true,
      render: (v, r) => (
        <div>
          <div style={{ fontSize: 13 }}>{v}</div>
          {r.numeroFactureOriginal && (
            <div
              style={{
                fontSize: 11,
                color: "#6b7280",
                fontFamily: "monospace",
              }}
            >
              {t('detail.factures.numero', { numero: r.numeroFactureOriginal })}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('commun.date'),
      dataIndex: "dateFacture",
      key: "date",
      width: 100,
      align: "center",
      render: (v) => (
        <span style={{ fontSize: 12, color: "#6b7280" }}>{formatDate(v)}</span>
      ),
    },
    {
      title: t('commun.reclame'),
      dataIndex: "montantReclame",
      key: "recl",
      width: 130,
      align: "right",
      render: (v, r) =>
        Number(v) === 0 ? (
          <Tooltip title={t('detail.factures.montantNonRenseigne')}>
            <Button type="link" size="small" onClick={() => setFactureACorriger(r)}
              style={{ fontSize: 12, fontFamily: "monospace", color: "#f59e0b", fontWeight: 600, padding: 0 }}>
              {t('detail.factures.aCompleter')} <WarningOutlined />
            </Button>
          </Tooltip>
        ) : (
          <span style={{ fontSize: 12, fontFamily: "monospace" }}>
            {formatMontant(v)}
          </span>
        ),
    },
    {
      title: t('commun.retenu'),
      dataIndex: "montantRetenu",
      key: "ret",
      width: 130,
      align: "right",
      render: (v, r) => {
        if (v == null) return <span style={{ color: "#9ca3af" }}>-</span>;
        const partiel = v < r.montantReclame;
        return (
          <span
            style={{
              fontSize: 12,
              fontFamily: "monospace",
              fontWeight: 600,
              color: v === 0 ? "#dc2626" : partiel ? "#f97316" : GREEN,
            }}
          >
            {formatMontant(v)}
          </span>
        );
      },
    },
    {
      title: t('commun.statut'),
      dataIndex: "statutTraitement",
      key: "statut",
      width: 140,
      render: (v) => {
        const s = STATUT_FACTURE_MAP[v] || { label: v, color: "default" };
        return (
          <Tag
            color={s.color}
            style={{ borderRadius: 20, fontSize: 11, fontWeight: 500 }}
          >
            {s.label}
          </Tag>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 60,
      fixed: "right",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              {
                key: "modifier",
                icon: <EditOutlined />,
                label: t('detail.factures.menu.modifier'),
                onClick: () => setFactureACorriger(r),
              },
              {
                key: "traiter",
                icon: <CheckOutlined />,
                label:
                  r.statutTraitement === "EN_ATTENTE" ? t('detail.factures.menu.traiter') : t('detail.factures.menu.revoir'),
                onClick: () => setValiderModal({ open: true, facture: r }),
              },
              {
                key: "download",
                icon: <DownloadOutlined />,
                label: t('detail.factures.menu.telechargerScan'),
                disabled: !r.ossanGedDocumentId,
                onClick: () =>
                  telecharger(
                    r.ossanGedDocumentId,
                    `facture-${r.numeroFactureOriginal || r.factureTrackingId}.pdf`,
                  ),
              },
              { type: "divider" },
              {
                key: "delete",
                icon: <DeleteOutlined />,
                label: t('detail.factures.menu.supprimer'),
                danger: true,
                onClick: () =>
                  CBModal.confirm({
                    title: t('detail.factures.suppression.titre'),
                    content: t('detail.factures.suppression.contenu'),
                    okText: t('detail.factures.menu.supprimer'),
                    okButtonProps: { danger: true },
                    cancelText: t('commun.annuler'),
                    onOk: () => supprimerFacture(r.factureTrackingId),
                  }),
              },
            ],
          }}
        >
          <Button size="small" type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  const factures = dossier.factures || [];
  const nbPiecesMaturite =
    dossier.nbPiecesRecues != null && dossier.nbPiecesRequises != null
      ? `${dossier.nbPiecesRecues}/${dossier.nbPiecesRequises}`
      : null;

  const tabs = [
    /* ═══════ PIÈCES ADMINISTRATIVES - pilotées par le composant ChecklistPieces ═══════ */
    {
      key: "admin",
      label: (
        <span>
          <FileTextOutlined /> {t('commun.piecesAdministratives')}
          {nbPiecesMaturite && (
            <Tag style={{ marginLeft: 6, borderRadius: 10, fontSize: 11 }}>
              {nbPiecesMaturite}
            </Tag>
          )}
        </span>
      ),
      children: (
        <div style={{ padding: "4px 0" }}>
          <ChecklistPieces
            dossierTrackingId={dossier.dossierTrackingId}
            sinistreTrackingId={dossier.sinistreTrackingId}
            victimeTrackingId={dossier.victimeTrackingId}
            onDocumentTeleverse={() => { rafraichirDossier(); loadDocsGed(); }}
          />
        </div>
      ),
    },

    /* ═══════ FACTURES DE RÉCLAMATION ═══════ */
    {
      key: "factures",
      label: (
        <span>
          <DollarOutlined /> {t('detail.onglets.factures')}
          <Tag style={{ marginLeft: 6, borderRadius: 10, fontSize: 11 }}>
            {factures.length}
          </Tag>
        </span>
      ),
      children: (
        <>
          {factures.some((f) => f.statutTraitement === "EN_ATTENTE") && (
            <Alert
              showIcon
              type="info"
              style={{ borderRadius: 10, marginBottom: 12 }}
              message={t('detail.factures.enAttenteTraitement', { n: factures.filter((f) => f.statutTraitement === "EN_ATTENTE").length })}
            />
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 12,
            }}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{ background: GREEN, borderColor: GREEN }}
              onClick={() => setFactureModal(true)}
            >
              {t('detail.factures.ajouter')}
            </Button>
          </div>
          <div className="cb-card" style={{ padding: 0 }}>
            <Table
              className="cb-table"
              dataSource={factures}
              columns={factureColumns}
              rowKey="factureTrackingId"
              scroll={{ x: 900 }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('detail.factures.aucune')}
                  />
                ),
              }}
              pagination={false}
              summary={(data) => {
                if (data.length === 0) return null;
                const totalReclame = data.reduce(
                  (s, f) => s + (f.montantReclame || 0),
                  0,
                );
                const totalRetenu = data.reduce(
                  (s, f) => s + (f.montantRetenu || 0),
                  0,
                );
                return (
                  <Table.Summary.Row
                    style={{ background: GREEN_PALE, fontWeight: 700 }}
                  >
                    <Table.Summary.Cell index={0} colSpan={3}>
                      {t('detail.factures.totaux')}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      <span style={{ fontFamily: "monospace" }}>
                        {formatMontant(totalReclame)}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      <span style={{ fontFamily: "monospace", color: GREEN }}>
                        {formatMontant(totalRetenu)}
                      </span>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} colSpan={2} />
                  </Table.Summary.Row>
                );
              }}
            />
          </div>
        </>
      ),
    },

    /* ═══════ DOCUMENTS GED ═══════ */
    {
      key: "ged",
      label: (
        <span>
          <FolderOpenOutlined /> {t('detail.onglets.ged')}
          <Tag style={{ marginLeft: 6, borderRadius: 10, fontSize: 11 }}>
            {docsGed.length}
          </Tag>
        </span>
      ),
      children: (
        <Table
          className="cb-table"
          dataSource={docsGed}
          rowKey={(r) => r.ossanGedDocumentTrackingId || r.ossanGedDocumentId || r.titre}
          size="small"
          pagination={{ pageSize: 10 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('detail.ged.aucun')}
              />
            ),
          }}
          columns={[
            { title: t('detail.ged.colonnes.titre'), dataIndex: "titre", ellipsis: true },
            {
              title: t('detail.ged.colonnes.type'),
              dataIndex: "typeDocument",
              width: 220,
              render: (v) => (
                <Tag color="blue" style={{ borderRadius: 20 }}>
                  {TYPES_DOC_GED_MAP[v] || v?.replace(/_/g, " ") || "-"}
                </Tag>
              ),
            },
            {
              title: t('commun.date'),
              dataIndex: "dateDocument",
              width: 130,
              render: (v) => formatDate(v),
            },
            {
              title: "",
              width: 180,
              align: "right",
              render: (_, r) => (
                <Space size={4}>
                  <Button
                    size="small"
                    disabled={!r.ossanGedDocumentId}
                    onClick={() => setPreviewDoc(r)}
                    icon={<EyeOutlined />}
                  >
                    {t('detail.ged.apercu')}
                  </Button>
                  <Button
                    icon={<DownloadOutlined />}
                    size="small"
                    onClick={() => telecharger(r.ossanGedDocumentId, r.titre)}
                  >
                    PDF
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Layout title={t('detail.titre', { numero: dossier.numeroDossier })}>
      {/* ── En-tête ── */}
      <div style={{ marginBottom: 20 }}>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/reclamations")}
          style={{ paddingLeft: 0, marginBottom: 8 }}
        >
          {t('detail.retourListe')}
        </Button>
        <div className="cb-page-header">
          <div>
            <h1 className="cb-page-title">
              {t('detail.dossier')}{" "}
              <span style={{ color: GREEN }}>{dossier.numeroDossier}</span>
            </h1>
            <p className="cb-page-sub">
              {t('detail.ouvertLeRedacteur', { date: formatDate(dossier.dateOuverture), redacteur: dossier.redacteurNomComplet || "-" })}
            </p>
          </div>
          <Space size={12}>
            <BoutonMaturite
              sinistreTrackingId={dossier.sinistreTrackingId}
              victimeTrackingId={dossier.victimeTrackingId}
              onChange={loadDossier}
            />
            <Tag
              color={statut.color}
              style={{
                borderRadius: 20,
                fontSize: 13,
                padding: "4px 14px",
                fontWeight: 600,
              }}
            >
              {statut.label}
            </Tag>
          </Space>
        </div>
      </div>

      {/* ── Infos contextuelles ── */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <div className="cb-form-section">
            <div className="cb-form-section-title">
              <FolderOpenOutlined /> {t('detail.sinistre.titre')}
            </div>
            <Descriptions
              column={1}
              size="small"
              labelStyle={{ color: "#6b7280", width: 140 }}
            >
              <Descriptions.Item label={t('detail.sinistre.numero')}>
                <button
                  onClick={() =>
                    navigate(`/sinistres/${dossier.sinistreTrackingId}`)
                  }
                  style={{
                    background: "none",
                    border: "none",
                    color: GREEN,
                    fontWeight: 600,
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  {dossier.numeroSinistreLocal || "-"}
                </button>
              </Descriptions.Item>
              <Descriptions.Item label={t('detail.sinistre.typeDommage')}>
                <Tag
                  color={
                    dossier.typeDommage === "CORPOREL"
                      ? "red"
                      : dossier.typeDommage === "DECES"
                        ? "volcano"
                        : "orange"
                  }
                  style={{ borderRadius: 20 }}
                >
                  {dossier.typeDommage}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('detail.sinistre.dateAccident')}>
                {formatDate(dossier.dateAccident)}
              </Descriptions.Item>
            </Descriptions>
          </div>
        </Col>
        <Col xs={24} md={12}>
          <div className="cb-form-section">
            <div className="cb-form-section-title">
              <UserOutlined /> {t('commun.victime')}
            </div>
            <Descriptions
              column={1}
              size="small"
              labelStyle={{ color: "#6b7280", width: 140 }}
            >
              <Descriptions.Item label={t('detail.victime.nom')}>
                {dossier.victimeNomComplet || "-"}
              </Descriptions.Item>
              <Descriptions.Item label={t('commun.statut')}>
                <Tag
                  color={dossier.victimeType === "DECEDE" ? "red" : "orange"}
                  style={{ borderRadius: 20 }}
                >
                  {dossier.victimeType || "-"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('detail.victime.dateNaissance')}>
                {formatDate(dossier.victimeDateNaissance)}
              </Descriptions.Item>
            </Descriptions>
          </div>
        </Col>
      </Row>

      {/* ── KPI ── */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col xs={12} md={6}>
          <Kpi
            label={t('commun.pieces')}
            value={
              dossier.nbPiecesRequises != null
                ? `${dossier.nbPiecesRecues ?? 0}/${dossier.nbPiecesRequises}`
                : "-"
            }
            sub={dossier.estMur
              ? t('detail.kpi.dossierMur')
              : dossier.piecesCompletes && dossier.consolidationRequise && !dossier.estConsolidee
                ? t('detail.kpi.attenteConsolidation')
                : t('detail.kpi.enCours')}
            icon={<FileTextOutlined />}
            color={dossier.estMur ? GREEN : "#374151"}
          />
        </Col>
        <Col xs={12} md={6}>
          <Kpi
            label={t('commun.factures')}
            value={factures.length}
            icon={<DollarOutlined />}
            sub={t('detail.kpi.enAttente', { n: factures.filter((f) => f.statutTraitement === "EN_ATTENTE").length })}
          />
        </Col>
        <Col xs={12} md={6}>
          <Kpi
            label={t('commun.totalReclame')}
            value={formatMontant(dossier.montantTotalReclame)}
            icon={<InboxOutlined />}
            color="#374151"
          />
        </Col>
        <Col xs={12} md={6}>
          <Kpi
            label={t('commun.totalRetenu')}
            value={formatMontant(dossier.montantTotalRetenu)}
            icon={<CheckOutlined />}
            color={GREEN}
            sub={
              dossier.montantTotalReclame > 0
                ? t('detail.kpi.pourcentageReclame', { pct: Math.round((dossier.montantTotalRetenu / dossier.montantTotalReclame) * 100) })
                : null
            }
          />
        </Col>
      </Row>

      {/* ── Onglets ── */}
      <div className="cb-card">
        <Tabs items={tabs} defaultActiveKey="admin" />
      </div>

      {/* ── Modals ── */}
      <NouvelleFactureModal
        open={factureModal}
        onClose={() => setFactureModal(false)}
        dossierId={id}
        onCreated={loadDossier}
      />
      <NouvelleFactureModal
        open={!!factureACorriger}
        facture={factureACorriger}
        onClose={() => setFactureACorriger(null)}
        dossierId={dossier?.dossierTrackingId}
        onCreated={() => rafraichirDossier()}
      />
      <ValiderFactureModal
        open={validerModal.open}
        facture={validerModal.facture}
        onClose={() => setValiderModal({ open: false, facture: null })}
        onValidated={loadDossier}
      />
      <DocumentPreviewModal
        doc={previewDoc}
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
      />
    </Layout>
  );
}
